import React, {useEffect, useRef, useState, useCallback} from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import styled from 'styled-components';
import Driver from 'driver.js';
import {buildSortMetricQueryParams} from '../util/MediaQueryParamsBuilder';
import 'driver.js/dist/driver.min.css';
import Constants, {AnnotationModes, SortMetric} from '../../constants/Constants';
import cssConstants from '../../constants/cssConstants';
import MediaDetails from '../details/MediaDetails';
import PizzaTracker from '../display/PizzaTracker';
import SmartPlayer from '../display/SmartPlayer';
import config from '../../service/config';
import {showWatchProductMessage} from '../../util/MarketingHelper';
import MediaTabWrapper from '../tabs/MediaTabsWrapper';
import Conversation from '../conversation/Conversation';
import MarketingPane from '../marketing/MarketingPane';
import ReactionsEmojiMenu from '../reactions/ReactionsMenu';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {updateSelectedConversationState} from '../../context/selectedConversationProvider';
import {useQueryParam, StringParam} from 'use-query-params';
import {ControlButton, TscButtonType, Glyphs, TscThemeNames, TscStyles, TscThemeName} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import {IConversationData} from '../../model/conversationModel';
import {conversationsStore, updateConversationsFromServer, updateConversationsState} from '../../context/conversationsProvider';
import {IComment} from '../../model/commentModel';
import collectionApi from '../../service/collectionApi';
import conversationApi from '../../service/conversationApi';
import {updateCommentsState} from '../../context/commentsProvider';
import {SetupWorkerState} from '../../model/workerState';
import {annotationManagerStore, updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import Image from '../display/Image';
import {themeStore} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';
import logging from '../../service/logging';
import {isMobile, isSafari} from 'react-device-detect';
import {updateReactionsFromServer} from '../../context/reactionsProvider';
import ReactionsImageWidget from '../reactions/ReactionsImageWidget';
import ReactionUIButton from '../reactions/ReactionUIButton';
import LocalStorageWrapper from '../util/LocalStorageWrapper';
import Axios, {CancelTokenSource} from 'axios';
import CollectionNavigationPane from './CollectionNavigationPane';
import {displayModes, events} from '@techsmith/techsmith-smartplayer';
import {importMediaInterface} from '../../hooks/dynamicImport';

smoothscroll.polyfill();

const cssAspectRatio = '--media-sizing-aspect-ratio';

const ninetyDegrees = 90;
const twoSeventyDegrees = 270;
const gap = '.75rem';
const reactionsContainerHeight = '2.125rem';

const ViewPageGrid = styled.div<{annotationMode: AnnotationModes; isConversationPaneOpen: boolean; mediaWidth: number; mediaHeight: number; marketingPaneVisible: boolean; smartPlayerDisplayMode: string}>`
   display: grid;
   ${props => props.smartPlayerDisplayMode === displayModes.Theater && 'flex-direction: column;'};
   width: 100%;
   height: ${props => props.smartPlayerDisplayMode === displayModes.Theater ? '100%' : 'max-content'};
   padding: ${props => props.smartPlayerDisplayMode === displayModes.Theater ? 0 : `${cssConstants.viewPageMeasurements.layoutGridVerticalMargin} ${cssConstants.viewPageMeasurements.layoutGridHorizontalMargin}`};

   grid-template-areas:
      ${props => props.marketingPaneVisible && '\'leftGutter marketing marketing rightGutter\''}
      'leftGutter player conversation rightGutter'
      'leftGutter reactions conversation rightGutter'
      'leftGutter mediaDetails conversation rightGutter'
      'leftGutter manage conversation rightGutter';
   grid-template-rows:
      ${props => props.marketingPaneVisible && 'min-content'}
      min-content
      min-content
      min-content
      1fr;

   ${props => props.smartPlayerDisplayMode === displayModes.Theater && props.marketingPaneVisible &&
      `grid-template-areas:
         'marketing marketing marketing marketing'
         'player player player player'
         'leftGutter reactions ${props.isConversationPaneOpen ? 'conversation' : 'reactions'} rightGutter'
         'leftGutter mediaDetails ${props.isConversationPaneOpen ? 'conversation' : 'mediaDetails'} rightGutter'
         'leftGutter manage ${props.isConversationPaneOpen ? 'conversation' : 'manage'} rightGutter';
      `}

   ${props => props.smartPlayerDisplayMode === displayModes.Theater && !props.marketingPaneVisible &&
      `grid-template-areas:
         'player player player player'
         'leftGutter reactions ${props.isConversationPaneOpen ? 'conversation' : 'reactions'} rightGutter'
         'leftGutter mediaDetails ${props.isConversationPaneOpen ? 'conversation' : 'mediaDetails'} rightGutter'
         'leftGutter manage ${props.isConversationPaneOpen ? 'conversation' : 'manage'} rightGutter';
      `}

   ${props => props.smartPlayerDisplayMode === displayModes.Theater && props.marketingPaneVisible &&
      `grid-template-rows:
         min-content
         auto
         min-content
         min-content
         1fr;
      `}

   ${props => props.smartPlayerDisplayMode === displayModes.Theater && !props.marketingPaneVisible &&
      `grid-template-rows:
         auto
         min-content
         min-content
         1fr;
      `}

   grid-template-columns: auto auto auto auto;
   ${props => props.smartPlayerDisplayMode === displayModes.Theater &&
      `grid-template-columns:
         1fr
         minmax(auto, ${cssConstants.contentWidth})
         ${cssConstants.conversationPanelWidth}
         1fr;
      `}

   justify-content: center;

   @media screen and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      padding: 0;
      width: 100%;
      height: ${props => props.isConversationPaneOpen ? '100%' : 'min-content'};
      display: flex;
      flex-direction: column;
      justify-content: start;
   }
`;

const LeftGutterContainer = styled.div`
   grid-area: leftGutter;
`;

const RightGutterContainer = styled.div`
   grid-area: rightGutter;
`;

const MarketingContainer = styled.div`
   grid-area: marketing;
   display: flex;
   justify-content: center;
`;

const CollectionNavigationContainer = styled.div`
   display: flex;
   justify-content: center;
   max-width: 64rem;
   width: 100%;
   margin: .5rem auto;
`;

const MediaContainer = styled.div`
   width: 100%;
   min-width: 0;
   grid-area: player;
   display: flex;
   flex-direction: column;
   align-content: center;
   align-items: center;
   margin-bottom: ${gap};

   @media screen and (max-width: ${cssConstants.conversationsBreakpoint}px)  {
      margin-bottom: 0;
   }
`;

const MediaDetailsContainer = styled.div`
   grid-area: mediaDetails;
   justify-self: end;
   width: 100%;
   min-width: 0;
`;

const ReactionsContainer = styled.div`
   grid-area: reactions;
   display: flex;
   width: 100%;
   align-content: space-around;
   flex-wrap: wrap;
   justify-content: center;
   align-items: center;
   height: ${reactionsContainerHeight};
   justify-self: end;

   @media screen and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      margin: .5rem 0;
   }
`;

const ReactionsMenuContainer = styled.div<{conversationsEnabled: boolean; theme: TscThemeNames}>`
   display: flex;
   justify-content: flex-end;
   border-radius: ${TscStyles.border.radius.lg};
   border: 1px solid ${props => props.theme === TscThemeName.dawn ? TscStyles.color.ui.dawn.mediumDark : TscStyles.color.ui.dusk.medium};
   overflow: hidden;
   height: 2.125rem;
   margin-bottom: ${gap};

   .toggle-conversations-panel {
      ${props => props.conversationsEnabled && `border-radius: 0 ${TscStyles.border.radius.md} ${TscStyles.border.radius.md} 0;`};
   }
`;

const ManagementContainer = styled.div<{conversationPaneOpen: boolean}>`
   grid-area: manage;
   display: flex;
   flex-direction: column;
   width: 100%;
   justify-self: end;
   margin-top: ${gap};

   /* needed to tie/beat 'more' menu of media details window on vertically short viewport */
   @media screen and (min-width: ${cssConstants.contextMenuBreakpoint}px) {
      z-index: ${cssConstants.zIndex.viewPageElement};
   }

   @media screen and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      ${props => props.conversationPaneOpen && 'display: none;'};
   }
`;

const ConversationContainer = styled.div<{conversationPaneOpen: boolean; smartPlayerDisplayMode: string; marketingPaneVisible: boolean; isLti: boolean}>`
   grid-area: conversation;
   position: ${props => props.conversationPaneOpen ? 'sticky' : 'initial'};
   top: calc(${props => props.isLti ? '0px' : `var(${cssConstants.variables.headerHeight}, ${cssConstants.headerHeight})`} + ${cssConstants.viewPageMeasurements.layoutGridHorizontalMargin} + ${props => props.marketingPaneVisible ? gap : '0px'} + ${props => props.smartPlayerDisplayMode === displayModes.Theater ? gap : '0px'});
   overflow: auto;
   height: calc(100dvh - ${props => props.isLti ? '0px' : `var(${cssConstants.variables.headerHeight}, ${cssConstants.headerHeight}) - var(${cssConstants.variables.marketingBannerHeight}, 0px)`} - ${cssConstants.viewPageMeasurements.layoutGridHorizontalMargin} - ${gap});
   width: ${props => props.smartPlayerDisplayMode === displayModes.Theater || !props.conversationPaneOpen ? 'auto' : 'max-content'};
   margin-left: ${props => props.conversationPaneOpen ? '.75rem' : '0'};
   ${props => props.smartPlayerDisplayMode === displayModes.Theater && 'padding-top: .5rem;'}
   box-shadow: 0 3px 6px rgba(0,0,0,.15), 0 0 2px rgba(0,0,0,.1);
   box-sizing: border-box;
   border-radius: ${TscStyles.border.radius.xl};

   @media screen and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      width: auto;
      flex: 1 1 auto;
      height: 100%;
      border-radius: 0;
   }

   @media screen and (min-height: ${cssConstants.mobileHeightBreakpoint}px) and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      margin-left: 0;
   }

   @media screen and (max-height: ${cssConstants.mobileHeightBreakpoint}px) and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      ${props => props.conversationPaneOpen && 'min-height: 5rem;'}
      margin-left: 0;
      overflow: visible;
   }

   &.driver-highlighted-element {
      top: 0 !important;
   }
`;

const MediaPageWrapper = styled.div<{theme: TscThemeNames}>`
   display: flex;
   flex-grow: 1;
   justify-content: center;
   width: 100%;
   align-self: center;

   @media screen and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      height: calc(100svh - var(${cssConstants.variables.headerHeight}, ${cssConstants.headerHeight}));
   }
`;

export const MediaBase = styled.div<{mediaHeight: number; isSafari: boolean; smartPlayerDisplayMode: string; isVideo: boolean; isReactionsEnabled: boolean; isConversationPaneOpen: boolean}>`
   overflow: auto;
   aspect-ratio: var(${cssAspectRatio}, 1920 / 1080);
   display: grid;
   width: 100vw;
   max-width: 100%;
   height: fit-content;
   max-height: min(${props => props.isVideo ? `${props.mediaHeight}px` : `calc(${props.mediaHeight}px * max(1, var(${cssConstants.variables.zoom}, 1)))`}, calc(100svh - ${props => props.smartPlayerDisplayMode === displayModes.Theater ? gap : `${gap} - ${cssConstants.viewPageMeasurements.layoutGridHorizontalMargin}`} - var(${cssConstants.variables.headerHeight}, ${cssConstants.headerHeight}) - var(${cssConstants.variables.marketingBannerHeight}, 0px) - ${props => props.isReactionsEnabled ? reactionsContainerHeight : '0px'} - var(${cssConstants.variables.mediaDetailsHeight}, 0px)));
   position: relative;
   ${props => props.smartPlayerDisplayMode !== displayModes.Theater && props.isVideo && `border-radius: ${TscStyles.border.radius.lg};`}

   &.pizza-tracker {
      aspect-ratio: 10 / 6;
      width: 100%;

      #tscMediaContent {
         height: 100%;
         width: 100%;
      }
   }

   /* Safari fullscreen fix for safari v15.x and lower, because our images trigger the fullscreen zoom element to be a child of */
   /* this element, safari has a bug where it still lets some parent styling of the fullscreen element bleed down and it screws up */
   /* full-screen rendering of the sibling, in this case, the zoom mechanics of images, (version 16.x appears to do better and maybe this can go after adoption */
   &:-webkit-full-screen {
      margin-left: 0;
      max-width: 100vw;
      max-height: 100vh;
      height: 100vh;
      width: 100vw;
      border-radius: 0;
   }

   @media screen and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      border-radius: 0;
   }

   @media screen and (min-height: ${cssConstants.mobileHeightBreakpoint}px) and (max-width: ${cssConstants.conversationsBreakpoint}px)  {
      ${props => props.isConversationPaneOpen && `max-height: calc(50svh - var(${cssConstants.variables.headerHeight}, ${cssConstants.headerHeight}) - ${props.isReactionsEnabled ? '3.125rem' : '0px'});`}
   }
`;

const AnnotationControlsHelper = styled.div<{theme: TscThemeNames}>`
   display: flex;
   flex-direction: column;
   align-items: center;
   text-align: center;
   margin-top: 0.5rem !important;
   margin-bottom: 0.5rem;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.light : TscStyles.color.text.mediumDark};
   font-family: ${TscStyles.font.family.primary};
   font-size: 0.875rem;
`;

export const AnnotationControlsHelperHeader = styled.div`
   padding: .25rem 0;
   font-weight: 600;
   font-size: 1.125rem;
`;

const ConversationReactionsButton = styled.div<{theme: TscThemeNames}>`
   display: flex;
   height: 2.125rem;
   outline: none;
`;

const ReactionConversationToggleButtonLabel = styled.span`
   display: inline-block;
   margin-left: .5rem;

   @media screen and (max-width: ${cssConstants.contentBreakpoint}px) {
      display: none;
   }
`;

const ConversationVisibilityButton = styled.div<{theme: TscThemeNames; hideConversationButton: boolean; isLti: boolean; marketingPaneVisible: boolean}>`
   height: 2rem;
   display: ${props => props.hideConversationButton ? 'none' : 'block'};
   margin-left: ${gap};
   align-self: flex-start;
   z-index: ${cssConstants.zIndex.conversation};
   position: sticky;
   top: 0;
   outline: none;

   @media screen and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      top: ${props => props.isLti ? cssConstants.viewPageMeasurements.layoutGridVerticalMargin : '3.5rem'};
      margin-top: ${props => props.isLti ? '0' : props.marketingPaneVisible ? '4.5rem' : '0.5rem'};
      right: 1.25rem;
      position: absolute;
   }
`;

const getCommentOrReplyById = (commentsToSearch: IComment[], commentId: string) => {
   const flatComments = commentsToSearch.reduce((commentsList: IComment[], c: IComment) => commentsList.concat(c, ...c.replies), []);
   return flatComments.find((c: IComment) => c.id === commentId);
};

const getRotatedDimensions = (width: number, height: number, rotation: number) => {
   // not accounting for rotation other than 90/270 because it is entirely unexpected and therefore unnecessary math
   // below, if isRotated we invert height/width aspect ratio calc and pass in height instead of width for size constraint
   const isRotated = rotation === ninetyDegrees || rotation === twoSeventyDegrees;
   return [
      isRotated ? height : width,
      isRotated ? width : height
   ];
};

export const MediaContentBase: React.FC<IMediaContentProps & IStateMappedProps> = ({displayMediaTabs, annotationMode, conversations, conversationPaneOpen, theme, Hash, IsVideo, Width, Height, Rotate, IsYouTube, IsReady}) => {
   const {t} = useTranslation();
   const [conversationLoadingState, setConversationLoadingState] = SetupWorkerState(IsVideo);
   const [initialSelectedComment, setInitialSelectedComment] = useState(null);
   const [displayConversationButtonOverSmartPlayer, setDisplayConversationButtonOverSmartPlayer] = useState(true);
   const [conversationParam] = useQueryParam(Constants.queryParams.conversation, StringParam);
   const [collectionId] = useQueryParam('collection', StringParam);
   const [collectionSortOrder] = useQueryParam('sort', StringParam);
   const getCollectionNavigationDataCancellationToken = useRef<CancelTokenSource>(null);
   const [deepLinkedCommentIdCamelCase] = useQueryParam(Constants.queryParams.commentId, StringParam);
   const [deepLinkedCommentIdLowerCase] = useQueryParam(Constants.queryParams.commentid, StringParam);
   const deepLinkedCommentId = deepLinkedCommentIdCamelCase ? deepLinkedCommentIdCamelCase : deepLinkedCommentIdLowerCase;
   const mediaContainerRef = useRef<HTMLDivElement>(null);
   const rootContainerRef = useRef<HTMLElement>(document.querySelector(':root'));
   const mediaPageWrapperRef = useRef<HTMLDivElement>(null);
   const playerSettingsOpen = useRef(false);
   const largeWidthCutoff = 510;
   const hasToggledConversationPaneRef = useRef(false);
   const conversationCoachMarkRef = useRef<Driver>(null);
   const [smartPlayerDisplayMode, setSmartPlayerDisplayMode] = useState<string>('normal');
   const [collectionNavigationResponse, setCollectionNavigationResponse] = useState(null);

   const onPlayerSettingsClose = useCallback(() => {
      setDisplayConversationButtonOverSmartPlayer(true);
      playerSettingsOpen.current = false;
   }, []);

   const onPlayerSettingsOpen = useCallback(() => {
      const currentMediaWidth = mediaPageWrapperRef.current.clientWidth;
      setDisplayConversationButtonOverSmartPlayer(currentMediaWidth >= largeWidthCutoff);
      playerSettingsOpen.current = true;
   }, []);

   const generateGetCollectionNavigationDataCancellationToken = () => {
      const currentCancellationToken = getCollectionNavigationDataCancellationToken.current;
      if (currentCancellationToken) {
         currentCancellationToken.cancel();
      }

      const newCancellationToken = Axios.CancelToken.source();
      getCollectionNavigationDataCancellationToken.current = newCancellationToken;
      return newCancellationToken;
   };

   const getCollectionNavigationData = async () => {
      if (collectionId) {
         setCollectionNavigationResponse(await collectionApi.getCollectionNavigationData(collectionId, Hash, buildSortMetricQueryParams(collectionSortOrder as SortMetric), generateGetCollectionNavigationDataCancellationToken()));
      }
   };

   useEffect(() => {
      void getCollectionNavigationData();
      void importMediaInterface().then(mediaInterface => {
         mediaInterface.addEventListener(events.External.PlayerSettingsClose, onPlayerSettingsClose);
         mediaInterface.addEventListener(events.External.PlayerSettingsOpen, onPlayerSettingsOpen);
      });

      return () => {
         void importMediaInterface().then(mediaInterface => {
            mediaInterface.removeEventListener(events.External.PlayerSettingsClose, onPlayerSettingsClose);
            mediaInterface.removeEventListener(events.External.PlayerSettingsOpen, onPlayerSettingsOpen);
         });
      };
   }, []);

   const updateSelectedConversation = async () => {
      try {
         setConversationLoadingState({isWorking: true, hasError: false});
         const updatedComments: IComment[] = conversationParam ? await conversationApi.getConversationComments(conversationParam) : [];
         const selectedComment = (deepLinkedCommentId && getCommentOrReplyById(updatedComments, deepLinkedCommentId)) || null;
         
         const mediaInterface = await importMediaInterface();
         selectedComment && mediaInterface.addEventListener(events.ExternalVideo.VideoLoadedData, () => {
            mediaInterface.currentTime = selectedComment.time;
         });

         setInitialSelectedComment(selectedComment);
         updateCommentsState({comments: updatedComments});
         updateSelectedConversationState({VideoReviewId: conversationParam, SelectedComment: selectedComment});
         updateAnnotationManagerState({annotationMode: selectedComment?.annotation ? AnnotationModes.display : AnnotationModes.none});
         setConversationLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         setConversationLoadingState({isWorking: false, hasError: true});
         logging.error('Failed to update selected conversation: ', e);
         e.status === Constants.statusCodes.notFound && config.watchConfig.isOwnerOrAdmin && updateConversationsState({conversationPaneOpen: false});
         e.status === Constants.statusCodes.notFound && (config.featureSwitches.IsScreencast || config.watchConfig.isOwnerOrAdmin) && updateSelectedConversationState({VideoReviewId: '', Title: null, SelectedComment: null, ConversationHash: null});
      }
   };

   const toggleConversationPane = () => {
      hasToggledConversationPaneRef.current = true;
      updateConversationsState({conversationPaneOpen: !conversationPaneOpen});

      window.setTimeout(() => {
         document.getElementById('conversation-pane')?.focus({preventScroll: showWatchProductMessage()});
      }, Constants.getDomElementTimeout);
   };

   const shouldDisplayCoachMarkForConversations = (): boolean => {
      const conversationContainerElement = document.getElementById('conversation-container');
      const userHasCloseCoachMark = LocalStorageWrapper.getItem(Constants.localStorageKeys.conversationCoachMark.replace('{userId}', config.user.TechSmithId));
      return config.featureSwitches.IsScreencast && conversationCoachMarkRef.current && !isMobile && window.innerWidth > cssConstants.conversationsBreakpoint && !userHasCloseCoachMark && !!conversationContainerElement;
   };

   const displayCoachMarkForConversations = () => {
      if (shouldDisplayCoachMarkForConversations()) {
         conversationCoachMarkRef.current.highlight({
            element: '#conversation-container',
            popover: {
               title: t('conversations.coachMark.title'),
               description: `${t('conversations.coachMark.message')}${t('conversations.coachMark.screenReaderAlert')}`,
               position: 'left-top'
            }
         });

         setTimeout(() => {
            const coachMarkCloseButton = document.getElementsByClassName('driver-close-btn')[0] as HTMLElement;
            coachMarkCloseButton.ariaLabel = t('conversations.coachMark.screenReaderCloseButtonAriaLabel');
            coachMarkCloseButton && coachMarkCloseButton.focus();
         }, 0);
      }
   };

   const resizeHandler = useCallback(() => {
      const currentMediaWidth = mediaPageWrapperRef.current.clientWidth;
      const scrollBarIsPresent = document.getElementById('start-of-content') && (currentMediaWidth < document.getElementById('start-of-content').clientWidth);

      // We do not want to display this coach mark for conversations under the video
      if (conversationCoachMarkRef.current && conversationCoachMarkRef.current.isActivated && window.innerWidth <= cssConstants.conversationsBreakpoint) {
         // library does not have a destroy method. this is a hack around it.
         const coachMarkButton = document.getElementsByClassName('driver-close-btn')[0] as HTMLElement;
         if (coachMarkButton) {
            coachMarkButton.click();
         }
      }

      setDisplayConversationButtonOverSmartPlayer(currentMediaWidth >= largeWidthCutoff || !playerSettingsOpen.current);

      if (scrollBarIsPresent) {
         if (document.getElementById('tscMediaContent')) {
            document.getElementById('tscMediaContent').classList.add('scrollbar-visible');
         }
         if (document.getElementById('video-and-tab-section')) {
            document.getElementById('video-and-tab-section').classList.add('scrollbar-visible');
         }
      } else {
         if (document.getElementById('tscMediaContent')) {
            document.getElementById('tscMediaContent').classList.remove('scrollbar-visible');
         }
         if (document.getElementById('video-and-tab-section')) {
            document.getElementById('video-and-tab-section').classList.remove('scrollbar-visible');
         }
      }
   }, []);

   useEffect(() => {
      const selectedConversation = conversations?.length ?
         config.featureSwitches.UseSingleConversation ?
            conversations[0] :
            conversations.find(conversation => conversation.VideoReviewId === conversationParam) :
         null;

      selectedConversation && updateSelectedConversationState({Title: selectedConversation.Title, VideoReviewId: conversationParam, ConversationHash: selectedConversation.ConversationHash});

      displayCoachMarkForConversations();
   }, [conversations]);

   useEffect(() => {
      conversationCoachMarkRef.current?.reset();
      if (!conversationPaneOpen) {
         return;
      }

      const hideOnMobileLoad = !deepLinkedCommentId && !hasToggledConversationPaneRef.current && mediaPageWrapperRef.current.clientWidth < cssConstants.conversationsBreakpoint;
      if (IsYouTube || hideOnMobileLoad) {
         updateConversationsState({conversationPaneOpen: false});
      }
   }, [conversationPaneOpen]);

   useEffect(() => {
      void updateSelectedConversation();
      if (config.featureSwitches.EnableReactions && conversationParam) {
         void updateReactionsFromServer(conversationParam);
      }
   }, [conversationParam]);

   useEffect(() => {
      IsReady && updateConversationsFromServer(Hash);
   }, [IsReady, Hash]);

   useEffect(() => {
      const stageBgColor = theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light;
      const driver = new Driver({
         className: 'conversation-coach-mark',
         padding: 0,
         opacity: .5,
         allowClose: false,
         stageBackground: stageBgColor,
         keyboardControl: false,
         onDeselected: () => {
            LocalStorageWrapper.setItem(Constants.localStorageKeys.conversationCoachMark.replace('{userId}', config.user.TechSmithId), 'true');
         }
      });
      conversationCoachMarkRef.current = driver;

      window.addEventListener('resize', resizeHandler);

      return () => {
         window.removeEventListener('resize', resizeHandler);
      };
   }, []);

   useEffect(() => {
      resizeHandler();
   }, [conversationPaneOpen]);

   useEffect(() => {
      const [rotatedMediaWidth, rotatedMediaHeight] = getRotatedDimensions(Width, Height, Rotate);
      rootContainerRef.current.style.setProperty(cssAspectRatio, `${rotatedMediaWidth} / ${rotatedMediaHeight}`); // We use this instead state so the css var can support the fallback aspect ratio
   }, [Height, Width, Rotate]);

   const onDisplayModeChange = (displayMode: string) => {
      setSmartPlayerDisplayMode(displayMode);
   };

   const showConversation: boolean =
      (
         (config.featureSwitches.UseSingleConversation && conversations?.length > 0) ||
         (config.watchConfig.isOwnerOrAdmin && !config.environmentData.IsLti) ||
         !!conversationParam
      ) && IsReady && !IsYouTube;

   const smartPlayerElement = IsReady ?
      IsVideo ?
         <SmartPlayer isEmbedded={false} onDisplayModeChange={onDisplayModeChange} /> :
         (<Image>
            <SmartPlayer isEmbedded={false} onDisplayModeChange={onDisplayModeChange} />
         </Image>) : <></>;

   const nativeMediaHeight = getRotatedDimensions(Width, Height, Rotate)[1];
   const showMarketingPane = showWatchProductMessage();
   const viewReviewId = conversations?.length > 0 ? conversations[0].VideoReviewId : null;

   return (
      <MediaPageWrapper ref={mediaPageWrapperRef} theme={theme} data-test-id="media-content">
         <ViewPageGrid annotationMode={annotationMode} isConversationPaneOpen={conversationPaneOpen} mediaWidth={Width} mediaHeight={Height} marketingPaneVisible={showMarketingPane} smartPlayerDisplayMode={smartPlayerDisplayMode}>
            <LeftGutterContainer/>
            {showMarketingPane && <MarketingContainer>
               <MarketingPane/>
            </MarketingContainer>}
            <MediaContainer ref={mediaContainerRef}>
               <MediaBase className={`media-base ${!IsReady ? 'pizza-tracker' : ''}`} mediaHeight={nativeMediaHeight} isSafari={isSafari} smartPlayerDisplayMode={smartPlayerDisplayMode} isVideo={IsVideo} isReactionsEnabled={config.featureSwitches.EnableReactions} isConversationPaneOpen={showConversation && conversationPaneOpen}>
                  {IsReady ? smartPlayerElement : <PizzaTracker isOwnerOrAdmin={config.watchConfig.isOwnerOrAdmin} />}
               </MediaBase>
               {annotationMode === AnnotationModes.edit &&
                  <AnnotationControlsHelper theme={theme} data-test-id="annotation-controls-helper">
                     <AnnotationControlsHelperHeader>{t('annotation.annotationModeHeader')}</AnnotationControlsHelperHeader>
                     <div>{t('annotation.annotationModeText')}</div>
                  </AnnotationControlsHelper>
               }
            </MediaContainer>
            {showConversation && config.featureSwitches.EnableReactions && <ReactionsContainer>
               <ConversationReactionsButton id="reactions-conversation-visibility-button" theme={theme}>
                  {!IsVideo && !IsYouTube && viewReviewId && <ReactionsImageWidget isEmbedded={false}></ReactionsImageWidget>}
                  <ReactionsMenuContainer conversationsEnabled={!!viewReviewId} theme={theme}>
                     <ReactionsEmojiMenu isEmbedded={false} conversationId={viewReviewId}></ReactionsEmojiMenu>
                     <ReactionUIButton
                        isEmbedded={false}
                        isActive={conversationPaneOpen}
                        fontSize={0.875}
                        hasBorder={false}
                        padding={'0 .75rem'}
                        title={conversationPaneOpen ? t('conversations.hideConversationPane') : t('conversations.showConversationPane')}
                        className={'toggle-conversations-panel'}
                        onClick={toggleConversationPane}
                     >
                        <Glyphs.Comment16x16/>
                        <ReactionConversationToggleButtonLabel>{t('conversations.commentsButtonLabel')}</ReactionConversationToggleButtonLabel>
                     </ReactionUIButton>
                  </ReactionsMenuContainer>
               </ConversationReactionsButton>
            </ReactionsContainer>}
            <ConversationContainer id="conversation-container" conversationPaneOpen={showConversation && conversationPaneOpen} smartPlayerDisplayMode={smartPlayerDisplayMode} marketingPaneVisible={showMarketingPane} isLti={config.environmentData.IsLti}>
               {showConversation && <Conversation initialSelectedComment={initialSelectedComment} conversationLoadingState={conversationLoadingState} setConversationLoadingState={setConversationLoadingState} smartPlayerDisplayMode={smartPlayerDisplayMode} marketingPaneVisible={showMarketingPane} />}
               {showConversation && !config.featureSwitches.EnableReactions && (
                  <ConversationVisibilityButton tabIndex={-1} id="conversation-visibility-button" theme={theme} hideConversationButton={conversationPaneOpen || !displayConversationButtonOverSmartPlayer} isLti={config.environmentData.IsLti} marketingPaneVisible={showMarketingPane}>
                     <ControlButton
                        themeName={theme}
                        buttonType={TscButtonType.secondary}
                        isFullWidth={false}
                        onClick={toggleConversationPane}
                        title={conversationPaneOpen ? t('conversations.hideConversationPane') : t('conversations.showConversationPane')}
                        testId="toggle-conversation-visibility-button"
                        glyph={<Glyphs.Comment16x16/>}
                     />
                  </ConversationVisibilityButton>
               )}
            </ConversationContainer>
            <MediaDetailsContainer>
               {collectionNavigationResponse && <CollectionNavigationContainer>
                  <CollectionNavigationPane sortMetric={collectionSortOrder} collectionId={collectionId} collectionNavigationResponse={collectionNavigationResponse} />
               </CollectionNavigationContainer>}
               <MediaDetails showMediaLocation={!collectionNavigationResponse}/>
            </MediaDetailsContainer>
            <ManagementContainer conversationPaneOpen={showConversation && conversationPaneOpen}>
               {displayMediaTabs && <MediaTabWrapper/>}
            </ManagementContainer>
            <RightGutterContainer/>
         </ViewPageGrid>
      </MediaPageWrapper>
   );
};

export interface IMediaContentProps {
   displayMediaTabs: boolean;
}

export interface IStateMappedProps {
   conversations: IConversationData[];
   annotationMode: AnnotationModes;
   conversationPaneOpen: boolean;
   theme: TscThemeNames;
   Hash: string;
   IsVideo: boolean;
   Width: number;
   Height: number;
   Rotate: number;
   IsYouTube: boolean;
   IsReady: boolean;
}

const mapStatesToProps = (
   {displayMediaTabs}: IMediaContentProps,
   {annotationMode}: Partial<IStateMappedProps>,
   {conversations, conversationPaneOpen}: Partial<IStateMappedProps>,
   {theme}: Partial<IStateMappedProps>,
   {Hash, IsVideo, Width, Height, IsYouTube, IsReady, Rotate}: Partial<IStateMappedProps>
): IStateMappedProps & IMediaContentProps => ({displayMediaTabs, annotationMode, conversations, conversationPaneOpen, theme, Hash, IsVideo, Width, Height, Rotate, IsYouTube, IsReady});

export default withMemoizedContexts(mapStatesToProps, annotationManagerStore, conversationsStore, themeStore, mediaStore)(MediaContentBase);
